import debug from 'debug';
import { makeAutoObservable } from 'mobx';

import { IAnalyticsModel } from '@r-client/shared/data/analytics';
import {
  getExpirationCookie,
  GraphqlClient,
  isMobileWebView,
} from '@r-client/shared/data/client';
import { Reporting } from '@r-client/shared/data/error-reporting';
import { getWindow, TAuthPersistence } from '@r-client/shared/util/core';

import { GetUserQuery } from '../../graphql/get-user-query';
import { hasGlobalAuthSession } from './global-auth-model';
import {
  Guest,
  IAuthModel,
  IAuthModelOpts,
  IAuthUser,
  ISignOut,
  MaybeValue,
  User,
  Viewer,
} from './interface';

const log = debug('feature:auth');

const canSessionExist = (authPersistence?: TAuthPersistence) => {
  if (authPersistence?.type === 'cookie') {
    if (hasGlobalAuthSession()) {
      return true;
    }

    const cookieExists = !!getExpirationCookie(authPersistence.cookiePrefix);
    if (cookieExists) return true;

    // TODO: https://linear.app/republic/issue/PLAT-723/revert-webview-specific-r-client-auth-change
    // Until mobile adds the cookie, we'll assume the user is logged in and make the user query
    return isMobileWebView();
  }
  return false;
};

const createGuest = (): Guest => ({
  isAuthenticated: false,
  isAdmin: false,
  info: undefined,
});

const createUser = (info: NonNullable<IAuthUser>): User<IAuthUser> => ({
  isAuthenticated: true,
  isAdmin: info.isAdmin,
  info,
});

export class AuthModel implements IAuthModel {
  private userQuery: GetUserQuery;
  private client: GraphqlClient;
  private analytics: IAnalyticsModel | undefined;
  private reporting: Reporting;
  private authPersistence: TAuthPersistence = { type: 'none' };
  private canSessionExist = false;

  constructor(opts: IAuthModelOpts) {
    makeAutoObservable(this);
    this.analytics = opts?.analytics;
    this.client = opts?.client;
    this.reporting = opts.reporting;
    this.authPersistence = opts.authPersistence;

    log('UserQuery initiated!');

    this.canSessionExist = canSessionExist(this.authPersistence);
    this.userQuery = new GetUserQuery({
      lazy: !this.canSessionExist,
      client: opts?.client,
      context: {
        doNotBatch: true,
      },
    });
  }

  /**
   * This reloads the user object for logged in user.
   */
  reloadSession = () => {
    log('UserQuery reload forcefully!');
    // This quirky logic is required to cover the case
    // that a query can be initiated in the `constructor`
    this.canSessionExist = true;
    this.userQuery.subscribe();
    return this.userQuery.refetch();
  };

  /**
   * Sign Out user action.
   *
   * @param redirectTo to use to redirect user after successful sing out
   * @returns boolean true/false which refers success or failures.
   */
  signOut = async ({ redirectTo }: ISignOut): Promise<boolean> => {
    if (!canSessionExist(this.authPersistence)) return false;

    const logData = this.viewer?.isAuthenticated
      ? { user: this.viewer.info.id }
      : {};
    this.reporting.info('User is being signed out from r-client.', logData);

    const wasReset = await this.resetUser();
    if (!wasReset) return false;

    if (redirectTo)
      getWindow<{ location: Location }>()?.location.assign(redirectTo);

    return true;
  };

  resetUser = async (): Promise<boolean> => {
    try {
      this.analytics?.reset();
      this.userQuery.resetQuery();
      await this.client.clearStore();
    } catch (e) {
      log('Error reported to rollbar', e);
      this.reporting.error(e as Error);
      return false;
    }

    return true;
  };

  get viewer(): MaybeValue<Viewer> {
    if (!this.canSessionExist) return createGuest();
    if (!this.userQuery.data) return undefined;
    return this.userQuery.data.user
      ? createUser(this.userQuery.data.user)
      : createGuest();
  }

  get isLoading(): boolean {
    return this.userQuery.isLoading;
  }

  get errors() {
    return this.userQuery.queryErrors;
  }
}

export class AuthModelPrerender<TViewer = Viewer>
  implements IAuthModel<TViewer>
{
  viewer = undefined;
  isLoading = false;
  errors = undefined;

  constructor() {
    this.isLoading = true;
  }

  reloadSession() {
    return undefined;
  }

  signOut(): Promise<boolean> {
    return Promise.resolve(false);
  }
}
